import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, FormControlLabel, Switch, Chip, Alert,
  Tabs, Tab
} from '@mui/material';
import { Add, Edit, Delete, Star } from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';
import postingService from '../../services/posting.service';
import accountingService from '../../services/accounting.service';
import groupsService from '../../services/groups.service';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ py: 2 }}>{children}</Box>}</div>
);

// ==================== PARTY PROFILES TAB ====================
const PartyProfilesTab: React.FC = () => {
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [customerGroups, setCustomerGroups] = useState<any[]>([]);
  const [supplierGroups, setSupplierGroups] = useState<any[]>([]);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    profileName: '', partyType: 'CUSTOMER', isDefault: true,
    customerGroupId: '' as any, supplierGroupId: '' as any,
    receivableAccountId: '' as any, payableAccountId: '' as any,
    advanceAccountId: '' as any, bankAccountId: '' as any,
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try { setProfiles((await postingService.getPartyProfiles()).data); } catch { }
    try { setAccounts((await accountingService.getAccounts()).data); } catch { }
    try { setCustomerGroups((await groupsService.getCustomerGroups()).data); } catch { }
    try { setSupplierGroups((await groupsService.getSupplierGroups()).data); } catch { }
  };

  const handleOpen = (p?: any) => {
    setError('');
    if (p) {
      setEditId(p.id);
      setForm({
        profileName: p.profileName, partyType: p.partyType, isDefault: p.isDefault,
        customerGroupId: p.customerGroupId || '', supplierGroupId: p.supplierGroupId || '',
        receivableAccountId: p.receivableAccountId, payableAccountId: p.payableAccountId,
        advanceAccountId: p.advanceAccountId || '', bankAccountId: p.bankAccountId,
      });
    } else {
      setEditId(null);
      setForm({
        profileName: '', partyType: 'CUSTOMER', isDefault: true,
        customerGroupId: '', supplierGroupId: '',
        receivableAccountId: '', payableAccountId: '',
        advanceAccountId: '', bankAccountId: '',
      });
    }
    setDialog(true);
  };

  const handleSave = async () => {
    setError('');
    const payload = {
      ...form,
      customerGroupId: form.customerGroupId || null,
      supplierGroupId: form.supplierGroupId || null,
      receivableAccountId: Number(form.receivableAccountId) || 0,
      payableAccountId: Number(form.payableAccountId) || 0,
      advanceAccountId: form.advanceAccountId ? Number(form.advanceAccountId) : null,
      bankAccountId: Number(form.bankAccountId) || 0,
    };
    try {
      if (editId) await postingService.updatePartyProfile(editId, payload);
      else await postingService.createPartyProfile(payload);
      setDialog(false); loadAll();
    } catch (err: any) { setError(err.response?.data?.message || 'Error saving'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('posting.delete_confirm'))) return;
    try { await postingService.deletePartyProfile(id); loadAll(); } catch { }
  };

  const acctSelect = (labelKey: string, field: string, required = true) => (
    <TextField fullWidth select label={t(labelKey) + (required ? ' *' : '')} value={(form as any)[field] || ''}
      onChange={e => setForm({ ...form, [field]: e.target.value })} size="small" sx={{ mt: 1.5 }}>
      <MenuItem value="">—</MenuItem>
      {accounts.map((a: any) => <MenuItem key={a.id} value={a.id}>{a.accountCode} - {a.accountName}</MenuItem>)}
    </TextField>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>{t('posting.new')}</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.profile_name')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.party_type')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('posting.is_default')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.receivable_account')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.payable_account')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.advance_account')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.bank_account')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} width={80}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map(p => (
              <TableRow key={p.id} hover>
                <TableCell><strong>{p.profileName}</strong></TableCell>
                <TableCell>
                  <Chip label={p.partyType === 'CUSTOMER' ? t('subledger.customer') : t('subledger.supplier')}
                    size="small" color={p.partyType === 'CUSTOMER' ? 'primary' : 'secondary'} variant="outlined" />
                </TableCell>
                <TableCell align="center">{p.isDefault && <Star color="warning" fontSize="small" />}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{p.receivableAccountName}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{p.payableAccountName}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{p.advanceAccountName || '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{p.bankAccountName}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(p)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {profiles.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>{t('posting.no_profiles')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? t('posting.edit') : t('posting.new')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField fullWidth label={t('posting.profile_name')} value={form.profileName}
              onChange={e => setForm({ ...form, profileName: e.target.value })} size="small" />
            <TextField fullWidth select label={t('posting.party_type')} value={form.partyType}
              onChange={e => setForm({ ...form, partyType: e.target.value })} size="small">
              <MenuItem value="CUSTOMER">{t('subledger.customer')}</MenuItem>
              <MenuItem value="SUPPLIER">{t('subledger.supplier')}</MenuItem>
            </TextField>
          </Box>
          {form.partyType === 'CUSTOMER' && (
            <TextField fullWidth select label={t('posting.customer_group')} value={form.customerGroupId}
              onChange={e => setForm({ ...form, customerGroupId: e.target.value })} size="small" sx={{ mt: 1.5 }}>
              <MenuItem value="">{t('common.none')}</MenuItem>
              {customerGroups.map((g: any) => <MenuItem key={g.id} value={g.id}>{g.code} - {g.description}</MenuItem>)}
            </TextField>
          )}
          {form.partyType === 'SUPPLIER' && (
            <TextField fullWidth select label={t('posting.supplier_group')} value={form.supplierGroupId}
              onChange={e => setForm({ ...form, supplierGroupId: e.target.value })} size="small" sx={{ mt: 1.5 }}>
              <MenuItem value="">{t('common.none')}</MenuItem>
              {supplierGroups.map((g: any) => <MenuItem key={g.id} value={g.id}>{g.code} - {g.description}</MenuItem>)}
            </TextField>
          )}
          {acctSelect('posting.receivable_account', 'receivableAccountId')}
          {acctSelect('posting.payable_account', 'payableAccountId')}
          {acctSelect('posting.advance_account', 'advanceAccountId', false)}
          {acctSelect('posting.bank_account', 'bankAccountId')}
          <FormControlLabel sx={{ mt: 1 }} control={
            <Switch checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />
          } label={t('posting.is_default')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.profileName}>
            {editId ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ==================== PRODUCT PROFILES TAB ====================
const ProductProfilesTab: React.FC = () => {
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    profileName: '', isDefault: true, categoryId: '' as any,
    revenueAccountId: '' as any, expenseAccountId: '' as any, vatAccountId: '' as any,
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try { setProfiles((await postingService.getProductProfiles()).data); } catch { }
    try { setAccounts((await accountingService.getAccounts()).data); } catch { }
  };

  const handleOpen = (p?: any) => {
    setError('');
    if (p) {
      setEditId(p.id);
      setForm({
        profileName: p.profileName, isDefault: p.isDefault, categoryId: p.categoryId || '',
        revenueAccountId: p.revenueAccountId, expenseAccountId: p.expenseAccountId,
        vatAccountId: p.vatAccountId || '',
      });
    } else {
      setEditId(null);
      setForm({
        profileName: '', isDefault: true, categoryId: '',
        revenueAccountId: '', expenseAccountId: '', vatAccountId: '',
      });
    }
    setDialog(true);
  };

  const handleSave = async () => {
    setError('');
    const payload = {
      ...form,
      categoryId: form.categoryId || null,
      revenueAccountId: Number(form.revenueAccountId) || 0,
      expenseAccountId: Number(form.expenseAccountId) || 0,
      vatAccountId: form.vatAccountId ? Number(form.vatAccountId) : null,
    };
    try {
      if (editId) await postingService.updateProductProfile(editId, payload);
      else await postingService.createProductProfile(payload);
      setDialog(false); loadAll();
    } catch (err: any) { setError(err.response?.data?.message || 'Error saving'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('posting.delete_confirm'))) return;
    try { await postingService.deleteProductProfile(id); loadAll(); } catch { }
  };

  const acctSelect = (labelKey: string, field: string, required = true) => (
    <TextField fullWidth select label={t(labelKey) + (required ? ' *' : '')} value={(form as any)[field] || ''}
      onChange={e => setForm({ ...form, [field]: e.target.value })} size="small" sx={{ mt: 1.5 }}>
      <MenuItem value="">—</MenuItem>
      {accounts.map((a: any) => <MenuItem key={a.id} value={a.id}>{a.accountCode} - {a.accountName}</MenuItem>)}
    </TextField>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>{t('posting.new')}</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.profile_name')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('posting.is_default')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.revenue_account')} *</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.expense_account')} *</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('posting.vat_account')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} width={80}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map(p => (
              <TableRow key={p.id} hover>
                <TableCell><strong>{p.profileName}</strong></TableCell>
                <TableCell align="center">{p.isDefault && <Star color="warning" fontSize="small" />}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{p.revenueAccountName}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{p.expenseAccountName}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{p.vatAccountName || '—'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(p)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {profiles.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>{t('posting.no_product_profiles')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? t('posting.edit') : t('posting.new')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <TextField fullWidth label={t('posting.profile_name')} value={form.profileName}
            onChange={e => setForm({ ...form, profileName: e.target.value })} size="small" sx={{ mt: 1 }} />
          {acctSelect('posting.revenue_account', 'revenueAccountId')}
          {acctSelect('posting.expense_account', 'expenseAccountId')}
          {acctSelect('posting.vat_account', 'vatAccountId', false)}
          <FormControlLabel sx={{ mt: 1 }} control={
            <Switch checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />
          } label={t('posting.is_default')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.profileName}>
            {editId ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ==================== MAIN PAGE ====================
const PostingProfiles: React.FC = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>{t('posting.title')}</Typography>
      <Paper sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t('posting.party_profiles')} />
          <Tab label={t('posting.product_profiles')} />
        </Tabs>
      </Paper>
      <TabPanel value={tab} index={0}><PartyProfilesTab /></TabPanel>
      <TabPanel value={tab} index={1}><ProductProfilesTab /></TabPanel>
    </Box>
  );
};

export default PostingProfiles;
